import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchMyModels } from '../marketplace/marketplaceApi';
import type { MarketplaceModel, AuthHeadersFn } from '../marketplace/marketplaceTypes';
import {
  buildChannelRows, capacityText, modelOptions, assignBody,
  type ChannelState, type FeedSource,
} from './aiChannels';

/** How often to re-read the channel assignments while the matrix is open. */
const CHANNEL_POLL_MS = 10000;

interface MatrixLayerSectionProps {
  apiUrl: string;
  authHeaders: AuthHeadersFn;
  /** The feeds currently on the grid — one row each. */
  sources: FeedSource[];
  /** Mirror the assignments upward so the grid can label and overlay its cells. */
  onChannelsChange?: (state: ChannelState | null) => void;
}

/**
 * Per-feed AI layer picker for the matrix sidebar.
 *
 * One row per selected feed, each with its own model. Models are no longer
 * mutually exclusive across the deployment — `ai_channels` runs one per feed,
 * bounded by MAX_DETECTION_CHANNELS / MAX_SEG_CHANNELS, which is why the
 * capacity line is part of the UI rather than a surprise 409.
 *
 * Assignments are server-wide: every operator sees the same models on the same
 * feeds, and the boxes are burned into the composited feed for all viewers.
 *
 * Design: docs/superpowers/specs/2026-08-27-per-feed-ai-channels-design.md
 */
export default function MatrixLayerSection({
  apiUrl,
  authHeaders,
  sources,
  onChannelsChange,
}: MatrixLayerSectionProps) {
  // null means "could not ask", which modelOptions treats differently from
  // "none" — a node with no marketplace catalogue still gets a detection row.
  const [models, setModels] = useState<MarketplaceModel[] | null>(null);
  const [state, setState] = useState<ChannelState | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Read inside the poll without making them dependencies — either would
  // restart the interval on every render.
  const busyRef = useRef<string | null>(null);
  busyRef.current = busy;
  const notifyRef = useRef(onChannelsChange);
  notifyRef.current = onChannelsChange;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchMyModels(authHeaders);
        if (!cancelled) setModels(list);
      } catch {
        // Absent on nodes that never ran the marketplace migrations; the
        // fallback detection row covers them.
        if (!cancelled) setModels(null);
      }
    })();
    return () => { cancelled = true; };
  }, [authHeaders]);

  const read = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/drone/ai-channels`, { headers: authHeaders() });
      if (!res.ok) return;
      const data: ChannelState = await res.json();
      setState(data);
      notifyRef.current?.(data);
    } catch {
      /* transient — the next tick retries */
    }
  }, [apiUrl, authHeaders]);

  // Poll so another operator's assignment shows up here.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (busyRef.current || cancelled) return;
      await read();
    };
    tick();
    const timer = setInterval(tick, CHANNEL_POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [read]);

  const select = useCallback(async (source: FeedSource, value: string) => {
    setBusy(source.id);
    setError(null);
    try {
      const res = await fetch(`${apiUrl}/api/drone/ai-channels`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify(assignBody(source, value)),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        // A 409 carries the capacity reason, a 403 the licence one. Showing it
        // verbatim is the point: a silent refusal reads as a broken picker.
        throw new Error(body.detail || `Request failed: ${res.status}`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to assign model');
    } finally {
      setBusy(null);
      // Re-read either way: only the server knows what is actually running, so
      // the row must never advance on optimism.
      await read();
    }
  }, [apiUrl, authHeaders, read]);

  if (sources.length === 0) return null;

  const rows = buildChannelRows(sources, state?.channels || []);

  return (
    <div className="matrix-source-group">
      <div className="matrix-group-label">AI Layers</div>
      <div className="matrix-layer-note">One model per feed · affects all viewers</div>

      {rows.map(row => {
        const source = sources.find(s => s.id === row.sourceId)!;
        const opts = modelOptions(models, row.value, state);
        return (
          <label
            key={row.sourceId}
            className={`matrix-channel-row ${row.disabled ? 'disabled' : ''}`}
            title={row.reason || row.name}
          >
            <span className="matrix-source-name">{row.name}</span>
            <select
              className="matrix-channel-select"
              value={row.value}
              disabled={row.disabled || busy === row.sourceId}
              aria-label={`AI layer for ${row.name}`}
              onChange={(e) => select(source, e.target.value)}
            >
              {opts.map(o => (
                <option key={o.value || 'off'} value={o.value} disabled={o.disabled}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        );
      })}

      {state && (
        <div className="matrix-layer-note">{capacityText(state.used, state.limits)}</div>
      )}
      {state?.offload_available === false && (
        <div className="matrix-layer-note">
          No GPU offload here — only the main feed can run a model
        </div>
      )}
      {error && <div className="matrix-layer-error">{error}</div>}
    </div>
  );
}
